import { useTranslation } from 'react-i18next';
import type { SupportScope } from '@/api/support/supportApi.types';
import type { SupportMessageWithSendStatus } from '@/hooks/useSupport';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { formatIsoDateTimeToTimeOnly } from '@/utils/dateFormatting';
import { cn } from '@/lib/utils';
import { SupportMediaPreview } from './SupportMediaPreview';

interface SupportMessageBubbleProps {
  scope: SupportScope;
  message: SupportMessageWithSendStatus;
  isOwnMessage: boolean;
  onRetry: (message: SupportMessageWithSendStatus) => void;
}

export function SupportMessageBubble({
  scope,
  message,
  isOwnMessage,
  onRetry,
}: SupportMessageBubbleProps) {
  const { t } = useTranslation('support');

  return (
    <div className={cn('flex flex-col', isOwnMessage ? 'items-end' : 'items-start')}>
      {isOwnMessage === false && message.senderName !== null && message.senderName.length > 0 ? (
        <span className="mb-1 px-1 text-[11px] font-semibold text-zoomuted">
          {message.senderName}
        </span>
      ) : null}
      <div
        className={cn(
          'max-w-[80%] sm:max-w-md rounded-2xl px-4 py-2.5 space-y-2',
          isOwnMessage
            ? 'bg-zooprimary text-zoowhite rounded-br-sm'
            : 'bg-zoowhite border border-zooborder text-zooindigo rounded-bl-sm'
        )}
      >
        {message.attachments.map(function renderAttachment(attachment) {
          return (
            <SupportMediaPreview
              key={attachment.id}
              scope={scope}
              attachment={attachment}
              isOwnMessage={isOwnMessage}
            />
          );
        })}
        {message.body === null || message.body.length === 0 ? null : (
          <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
        )}
      </div>
      <div
        className={cn(
          'mt-1 flex items-center gap-2 text-[11px] text-zoomuted',
          isOwnMessage ? 'flex-row-reverse' : ''
        )}
      >
        <span>{formatIsoDateTimeToTimeOnly(message.createdAt)}</span>
        {message.sendStatus === 'sending' ? <LoadingSpinner className="w-3 h-3" /> : null}
        {message.sendStatus === 'failed' ? (
          <button
            type="button"
            onClick={function retry() {
              onRetry(message);
            }}
            className="flex items-center gap-1 text-zooerrortext font-semibold"
          >
            <Icon name="AlertCircle" className="text-xs" />
            {t('support:composer.retry')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
